// media-library-cropper.component.ts
import {
  Component,
  Input,
  Output,
  EventEmitter,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  LearmoModalRef,
  LearmoModalService,
} from '../../../../../../shared/services/learmo-modal.service';
import { ImageCroppedEvent, LoadedImage } from 'ngx-image-cropper';

@Component({
  selector: 'app-media-library-cropper',
  templateUrl: './media-library-cropper.component.html',
  styleUrls: ['./media-library-cropper.component.css'],
})
export class MediaLibraryCropperComponent {
  @Input() imgSpecs: {
    width?: number;
    height?: number;
    ratio?: number;
  } = {};

  @Output() onCropDone = new EventEmitter<File>();
  @Output() cropperOpenerChange = new EventEmitter<File | null>();

  @ViewChild('imageCropperContainer') cropperContent!: TemplateRef<any>;

  cropperRef!: LearmoModalRef;
  imageChangedEvent: File | null = null;
  imageCurrentName = '';
  imageEventFile!: ImageCroppedEvent;
  isCropperReady = false;
  croppedImageDimensions: { width: number; height: number } | null = null;
  croppedImageSize: number | null = null;

  constructor(private modalService: LearmoModalService) {}

  @Input('cropperOpener') set setCropperFile(file: File | null) {
    if (file) this.openCropper(file);
  }

  openCropper(file: File) {
    this.imageChangedEvent = file;
    this.imageCurrentName = file.name;
    this.isCropperReady = false;

    this.cropperRef = this.modalService.open(this.cropperContent, {
      title: 'Crop & Adjust Image',
      premium: true,
      size: 'xl',
      centered: true,
      transparent: true,
    });

    this.cropperRef.result.finally(() => {
      this.cropperOpenerChange.emit(null);
      this.resetCropper();
    });
  }

  imageCropped(event: ImageCroppedEvent) {
    this.imageEventFile = event;

    // Extract dimensions
    if (event.width && event.height) {
      this.croppedImageDimensions = {
        width: event.width,
        height: event.height,
      };
    }

    // Calculate size
    if (event.base64) {
      const base64Length =
        event.base64.length - (event.base64.indexOf(',') + 1);
      this.croppedImageSize = (base64Length * 3) / 4;
    }
  }

  imageLoaded(image: LoadedImage) {
    this.isCropperReady = true;
    console.log('Image loaded successfully', image);
  }

  loadImageFailed() {
    console.error('Failed to load image');
    this.isCropperReady = false;
  }

  doneCrop() {
    if (!this.imageEventFile?.base64) return;

    const file = this.dataURLtoFile(
      this.imageEventFile.base64,
      this.imageCurrentName
    );

    this.onCropDone.emit(file);
    this.closeCropper();
  }

  cancelCrop() {
    this.closeCropper();
  }

  private closeCropper() {
    this.resetCropper();
    this.cropperRef?.close();
  }

  private resetCropper() {
    this.imageChangedEvent = null;
    this.imageCurrentName = '';
    this.isCropperReady = false;
    this.croppedImageDimensions = null;
    this.croppedImageSize = null;
  }

  private dataURLtoFile(dataUrl: string, filename: string): File {
    const [header, body] = dataUrl.split(',');
    const mime = header.match(/:(.*?);/)?.[1] ?? 'image/png';
    const bstr = atob(body);
    const u8arr = new Uint8Array(bstr.length);
    for (let i = 0; i < bstr.length; i++) u8arr[i] = bstr.charCodeAt(i);
    return new File([u8arr], filename, { type: mime });
  }

  getRatioDisplay(): string {
    if (this.imgSpecs?.width && this.imgSpecs?.height) {
      return `${this.imgSpecs.width}px × ${this.imgSpecs.height}px`;
    }
    if (this.imgSpecs?.ratio) {
      const gcd = (a: number, b: number): number =>
        b === 0 ? a : gcd(b, a % b);
      const numerator = Math.round(this.imgSpecs.ratio * 100);
      const denominator = 100;
      const divisor = gcd(numerator, denominator);
      return `${numerator / divisor}:${denominator / divisor}`;
    }
    return 'Free aspect';
  }

  getCropperMinWidth(): number {
    return this.imgSpecs?.width || 128;
  }

  getCropperMinHeight(): number {
    return this.imgSpecs?.height || 128;
  }
}
